import { Injectable } from '@angular/core';
import { ConstantsFordProtect } from 'src/app/models/constants/constants-ford-protect';
import { ViaCep } from 'src/app/models/address/via-cep';

export interface ValidationResult {
	isValid: boolean;
	errorKey?: string;
	errorMessage?: string;
}

@Injectable({
	providedIn: 'root'
})
export class ValidationErrorMapperService {

	/**
	 * Validates if CEP format is correct for a given country
	 * @param cep - CEP value to validate
	 * @param country - Country code (BRA, ARG, etc)
	 * @returns ValidationResult
	 */
	validateCepFormat(cep: string, country: string): ValidationResult {
		if (!cep || cep.trim() === '') {
			return {
				isValid: false,
				errorKey: 'TRANSLATE.ERROR.CEP.REQUIRED'
			};
		}

		const cleanCep = cep.replace(/\D/g, '');

		switch (country) {
			case ConstantsFordProtect.BRA:
				if (cleanCep.length !== 8) {
					return {
						isValid: false,
						errorKey: 'TRANSLATE.ERROR.CEP.INVALID.FORMAT.BRA',
						errorMessage: `CEP must have exactly 8 digits for Brazil. Current: ${cleanCep.length}`
					};
				}
				break;

			case ConstantsFordProtect.ARG:
				if (cleanCep.length < 4 || cleanCep.length > 10) {
					return {
						isValid: false,
						errorKey: 'TRANSLATE.ERROR.CEP.INVALID.FORMAT.ARG',
						errorMessage: `CEP must have between 4 and 10 digits for Argentina. Current: ${cleanCep.length}`
					};
				}
				break;

			case ConstantsFordProtect.CHL:
				if (cleanCep.length !== 7) {
					return {
						isValid: false,
						errorKey: 'TRANSLATE.ERROR.CEP.INVALID.FORMAT.CHL',
						errorMessage: `CEP must have exactly 7 digits for Chile. Current: ${cleanCep.length}`
					};
				}
				break;

			case ConstantsFordProtect.COL:
				if (cleanCep.length < 5 || cleanCep.length > 6) {
					return {
						isValid: false,
						errorKey: 'TRANSLATE.ERROR.CEP.INVALID.FORMAT.COL',
						errorMessage: `CEP must have between 5 and 6 digits for Colombia. Current: ${cleanCep.length}`
					};
				}
				break;

			default:
				return {
					isValid: false,
					errorKey: 'TRANSLATE.ERROR.CEP.COUNTRY.NOT.SUPPORTED'
				};
		}

		return { isValid: true };
	}

	/**
	 * Validates if CEP response matches selected city and state
	 * @param viaCepResponse - Response from ViaCep API
	 * @param selectedCity - City selected by user
	 * @param selectedState - State code selected by user
	 * @returns ValidationResult
	 */
	validateCepMatchesCityState(
		viaCepResponse: ViaCep | null,
		selectedCity: string,
		selectedState: string
	): ValidationResult {

		if (!viaCepResponse) {
			return {
				isValid: false,
				errorKey: 'TRANSLATE.ERROR.CEP.NOT.FOUND'
			};
		}

		if (!viaCepResponse.city || !viaCepResponse.stateAbbreviation) {
			return {
				isValid: false,
				errorKey: 'TRANSLATE.ERROR.CEP.INCOMPLETE.DATA'
			};
		}

		// Normalize strings for comparison (case-insensitive, trim spaces)
		const viaCepCity = (viaCepResponse.city || '').trim().toUpperCase();
		const userCity = (selectedCity || '').trim().toUpperCase();
		const viaCepState = (viaCepResponse.stateAbbreviation || '').trim().toUpperCase();
		const userState = (selectedState || '').trim().toUpperCase();

		// Check state match
		if (viaCepState !== userState) {
			return {
				isValid: false,
				errorKey: 'TRANSLATE.ERROR.CEP.STATE.MISMATCH',
				errorMessage: `CEP belongs to ${viaCepState}, but ${userState} was selected`
			};
		}

		// Check city match
		if (viaCepCity !== userCity) {
			return {
				isValid: false,
				errorKey: 'TRANSLATE.ERROR.CEP.CITY.MISMATCH',
				errorMessage: `CEP belongs to ${viaCepCity}, but ${userCity} was selected`
			};
		}

		return { isValid: true };
	}

	/**
	 * Maps backend error messages to frontend i18n keys
	 * Parses common backend error patterns
	 * @param errorResponse - HTTP error response or error message
	 * @returns i18n key and parsed message
	 */
	mapBackendErrorToI18nKey(errorResponse: unknown): { key: string; details?: string } {

		// Handle various error response formats
		let errorMessage = '';
		const parsedError = errorResponse as {
			error?: {
				exceptionMessage?: string;
				message?: string;
				httpMessage?: string;
				errors?: { defaultMessage?: string }[];
			};
			exceptionMessage?: string;
			message?: string;
		};

		if (typeof errorResponse === 'string') {
			errorMessage = errorResponse;
		} else if (parsedError?.error?.exceptionMessage) {
			errorMessage = parsedError.error.exceptionMessage;
		} else if (parsedError?.exceptionMessage) {
			errorMessage = parsedError.exceptionMessage;
		} else if (parsedError?.error?.message) {
			errorMessage = parsedError.error.message;
		} else if (parsedError?.error?.httpMessage) {
			errorMessage = parsedError.error.httpMessage;
		} else if (parsedError?.message) {
			errorMessage = parsedError.message;
		} else if (parsedError?.error?.errors?.[0]?.defaultMessage) {
			errorMessage = parsedError.error.errors[0].defaultMessage;
		}

		errorMessage = errorMessage.toLowerCase();
		const normalizedErrorMessage = errorMessage
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '');

		// Pattern: CEP is invalid
		if (normalizedErrorMessage.includes('cep invalido') || normalizedErrorMessage.includes('invalid cep') ||
			normalizedErrorMessage.includes('cep not found')) {
			const cepMatch = errorMessage.match(/(\d+)/);
			return {
				key: 'TRANSLATE.ERROR.CEP.NOT.FOUND.BACKEND',
				details: cepMatch ? cepMatch[1] : undefined
			};
		}

		// Pattern: Product/plan missing insurance code configuration
		if (
			normalizedErrorMessage.includes('getinsurancecode') ||
			(normalizedErrorMessage.includes('insurancecode') && normalizedErrorMessage.includes('is null'))
		) {
			return {
				key: 'TRANSLATE.ERROR.CONTRACT.INSURANCE.CODE.MISSING'
			};
		}

		// Pattern: Product and coverage are incompatible
		if (
			normalizedErrorMessage.includes('product') &&
			normalizedErrorMessage.includes('coverage') &&
			normalizedErrorMessage.includes('incompat')
		) {
			return {
				key: 'TRANSLATE.ERROR.CONTRACT.PRODUCT.COVERAGE.INCOMPATIBLE'
			};
		}

		// Pattern: Active contract already exists for VIN/product/coverage
		if (
			normalizedErrorMessage.includes('ja existe um contrato criado e ativo para vin') ||
			normalizedErrorMessage.includes('already exists an active contract for vin')
		) {
			return {
				key: 'TRANSLATE.ERROR.CONTRACT.DUPLICATE.ACTIVE'
			};
		}

		// Pattern: Dealer not found in contract creation flow
		if (
			(normalizedErrorMessage.includes('dealers') && normalizedErrorMessage.includes('nao encontrado')) ||
			normalizedErrorMessage.includes('dealer not found for pandac')
		) {
			return {
				key: 'TRANSLATE.ERROR.CONTRACT.DEALER.NOT.FOUND'
			};
		}

		// Pattern: POPIMS integration failure
		if (
			normalizedErrorMessage.includes('popims') &&
			(
				normalizedErrorMessage.includes('erro ao enviar o contrato') ||
				normalizedErrorMessage.includes('erro ao tentar o processo de persistencia do contrato') ||
				normalizedErrorMessage.includes('error sending contract') ||
				normalizedErrorMessage.includes('error while trying to persist contract')
			)
		) {
			return {
				key: 'TRANSLATE.ERROR.CONTRACT.POPIMS.SEND.FAILURE'
			};
		}

		// Pattern: Brazil contract requires city/state
		if (
			normalizedErrorMessage.includes('cidade e estado do cliente sao obrigatorios') ||
			normalizedErrorMessage.includes('city and state are required for contract issuance in brazil')
		) {
			return {
				key: 'TRANSLATE.ERROR.CONTRACT.CUSTOMER.CITY.STATE.REQUIRED.BRA'
			};
		}

		// Pattern: Brazil state abbreviation invalid length
		if (
			normalizedErrorMessage.includes('sigla do estado para o brasil deve ter apenas 2 caracteres') ||
			normalizedErrorMessage.includes('state abbreviation for brazil must have 2 characters')
		) {
			return {
				key: 'TRANSLATE.ERROR.CONTRACT.CUSTOMER.STATE.INVALID.LENGTH.BRA'
			};
		}

		// Pattern: Invalid pricing rules in contract creation
		if (
			normalizedErrorMessage.includes('valor nao pode ser negativo') ||
			normalizedErrorMessage.includes('value cannot be negative')
		) {
			return {
				key: 'TRANSLATE.ERROR.CONTRACT.PRICE.NEGATIVE'
			};
		}

		if (
			normalizedErrorMessage.includes('acrescimo nao pode ser maior que 40') ||
			normalizedErrorMessage.includes('increase cannot be greater than 40')
		) {
			return {
				key: 'TRANSLATE.ERROR.CONTRACT.PRICE.INCREASE.MAX.LIMIT'
			};
		}

		// Pattern: VIN Product Copy missing
		if (
			normalizedErrorMessage.includes('vinproductcopy') &&
			(
				normalizedErrorMessage.includes('nao encontrado') ||
				normalizedErrorMessage.includes('not found')
			)
		) {
			return {
				key: 'TRANSLATE.ERROR.CONTRACT.VIN.PRODUCT.COPY.NOT.FOUND'
			};
		}

		// Explicit backend fallback when no readable message exists
		if (normalizedErrorMessage.includes('erro nao esperado na execucao da api')) {
			return {
				key: 'TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR'
			};
		}

		// Pattern: CEP belongs to a different city/state than user informed
		if (
			normalizedErrorMessage.includes('cep') &&
			normalizedErrorMessage.includes('pertence a') &&
			(
				normalizedErrorMessage.includes('cidade/estado informados') ||
				normalizedErrorMessage.includes('cidade/estado informado') ||
				normalizedErrorMessage.includes('cidade/estado selecionados') ||
				normalizedErrorMessage.includes('cidade/estado selecionado')
			)
		) {
			// Example: "cep 01010909 pertence a sao paulo/sp. cidade/estado informados: francisco morato/sp"
			// If UF is the same, mismatch is city; if UF differs, mismatch is state.
			const locationMatch = normalizedErrorMessage.match(
				/pertence a\s+.*?\/([a-z]{2}).*cidade\/estado\s+informad[oa]s?:\s+.*?\/([a-z]{2})/
			);

			if (locationMatch?.[1] && locationMatch?.[2]) {
				const cepUf = locationMatch[1];
				const informedUf = locationMatch[2];

				if (cepUf === informedUf) {
					return {
						key: 'TRANSLATE.ERROR.CEP.CITY.MISMATCH'
					};
				}

				return {
					key: 'TRANSLATE.ERROR.CEP.STATE.MISMATCH'
				};
			}

			return {
				key: 'TRANSLATE.ERROR.CEP.STATE.MISMATCH'
			};
		}

		// Pattern: State validation rule (state must have 2 characters)
		if (normalizedErrorMessage.includes('estado') && normalizedErrorMessage.includes('caractere')) {
			return {
				key: 'TRANSLATE.ERROR.ADDRESS.STATE.INVALID.FORMAT'
			};
		}

		// Pattern: Address incomplete
		if (normalizedErrorMessage.includes('endereco') && normalizedErrorMessage.includes('incompleto')) {
			return {
				key: 'TRANSLATE.ERROR.ADDRESS.INCOMPLETE.DATA'
			};
		}

		// Pattern: Address validation failed
		if (normalizedErrorMessage.includes('endereco') || normalizedErrorMessage.includes('address')) {
			return {
				key: 'TRANSLATE.ERROR.ADDRESS.INVALID'
			};
		}

		// Default fallback
		return {
			key: 'TRANSLATE.CONTRACT.CANNOT.BE.SAVE.CONTACT.ADM'
		};
	}

	/**
	 * Validates complete address data for a country
	 * @param address - Street/address name
	 * @param number - Street number
	 * @param city - City name
	 * @param state - State code
	 * @param zip - Postal/zip code
	 * @param country - Country code
	 * @returns ValidationResult
	 */
	validateCompleteAddress(
		address: string,
		number: string,
		city: string,
		state: string,
		zip: string,
		country: string
	): ValidationResult {

		// Check required fields
		const requiredFields = { address, number, city, state, zip };
		const missingFields = Object.entries(requiredFields)
			.filter(([_, value]) => !value || (typeof value === 'string' && value.trim() === ''))
			.map(([key, _]) => key);

		if (missingFields.length > 0) {
			return {
				isValid: false,
				errorKey: 'TRANSLATE.ERROR.ADDRESS.REQUIRED.FIELDS',
				errorMessage: `Missing required fields: ${missingFields.join(', ')}`
			};
		}

		// Validate CEP format
		const formatValidation = this.validateCepFormat(zip, country);
		if (!formatValidation.isValid) {
			return formatValidation;
		}

		// Validate state code format
		if (country === ConstantsFordProtect.BRA && state.length !== 2) {
			return {
				isValid: false,
				errorKey: 'TRANSLATE.ERROR.ADDRESS.STATE.INVALID.FORMAT',
				errorMessage: 'State code must have exactly 2 characters for Brazil'
			};
		}

		return { isValid: true };
	}
}
